"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useMemo, useState } from "react";
import { FiFileText, FiPlus } from "react-icons/fi";
import QuickNoteCard from "@/components/quick-note-card";
import { Button } from "@/components/ui";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cx } from "@/lib/tailwind-utils";
import { PatientDetail } from "@/types/patient";
import { QuickNoteSummary } from "@/types/quick-note";
import { quickNoteQueryOptions } from "@/api/provider/user/patient/quick-note-query";
import { useMemoWindowStore } from "@/stores/memo-window-store";
type MemoTab = "patient" | "appointment";
interface MemosSectionProps {
  patient: PatientDetail;
  shouldUseTwoColumns: boolean;
}
export default function MemosSection(t0) {
  const $ = _c(34);
  const {
    patient,
    shouldUseTwoColumns
  } = t0;
  const openMemoWindow = useMemoWindowStore(_temp);
  let t1;
  if ($[0] !== patient.id) {
    t1 = quickNoteQueryOptions.list(patient.id);
    $[0] = patient.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: quickNotes
  } = useQuery(t1);
  const [tab, setTab] = useState("patient");
  let t2;
  if ($[2] !== quickNotes) {
    t2 = quickNotes ?? [];
    $[2] = quickNotes;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const allNotes = t2;
  let t3;
  if ($[4] !== allNotes) {
    t3 = allNotes.filter(_temp2);
    $[4] = allNotes;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const appointmentMemoCount = t3.length;
  const patientMemoCount = allNotes.length - appointmentMemoCount;
  let t4;
  if ($[6] !== quickNotes || $[7] !== tab) {
    let t5;
    if ($[9] !== tab) {
      t5 = note_0 => tab === "appointment" ? Boolean(note_0.eventId) : !note_0.eventId;
      $[9] = tab;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = (quickNotes ?? []).filter(t5).sort(_temp3);
    $[6] = quickNotes;
    $[7] = tab;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const sortedNotes = t4;
  let t5;
  if ($[11] !== allNotes.length || $[12] !== appointmentMemoCount || $[13] !== patientMemoCount) {
    t5 = value => {
      const next = value as MemoTab;
      setTab(next);
      posthog.capture("memos_tab_selected", {
        tab: next,
        patientMemoCount,
        appointmentMemoCount,
        totalMemoCount: allNotes.length
      });
    };
    $[11] = allNotes.length;
    $[12] = appointmentMemoCount;
    $[13] = patientMemoCount;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleTabChange = t5;
  let t6;
  if ($[15] !== allNotes.length || $[16] !== appointmentMemoCount || $[17] !== handleTabChange || $[18] !== patientMemoCount || $[19] !== tab) {
    t6 = allNotes.length > 0 && <Tabs value={tab} onValueChange={handleTabChange}><TabsList><TabsTrigger value="patient" className="px-2 py-0.5 text-caption">Patient memos<span className="ml-1 text-foreground-tertiary">{patientMemoCount}</span></TabsTrigger><TabsTrigger value="appointment" className="px-2 py-0.5 text-caption">Appointment memos<span className="ml-1 text-foreground-tertiary">{appointmentMemoCount}</span></TabsTrigger></TabsList></Tabs>;
    $[15] = allNotes.length;
    $[16] = appointmentMemoCount;
    $[17] = handleTabChange;
    $[18] = patientMemoCount;
    $[19] = tab;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== openMemoWindow || $[22] !== patient) {
    t7 = <Button variant="secondary" size="sm" icon={FiPlus} onClick={() => openMemoWindow({
      patient
    })}>Add Memo</Button>;
    $[21] = openMemoWindow;
    $[22] = patient;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== allNotes.length || $[25] !== patient || $[26] !== shouldUseTwoColumns || $[27] !== sortedNotes || $[28] !== tab) {
    t8 = sortedNotes.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-8 text-center"><FiFileText className="size-5 text-foreground-muted" aria-hidden={true} /><p className="text-body text-foreground-secondary">{allNotes.length === 0 ? "No memos yet" : tab === "appointment" ? "No memos written on this patient's appointments" : "No memos written on this patient outside an appointment"}</p></div> : <div className={cx(shouldUseTwoColumns ? "columns-[16rem] gap-3" : "columns-1")}>{sortedNotes.map(note_1 => <div key={note_1.id} className="mb-3 break-inside-avoid"><QuickNoteCard note={note_1} size="md" patient={patient} showAppointmentTag={true} /></div>)}</div>;
    $[24] = allNotes.length;
    $[25] = patient;
    $[26] = shouldUseTwoColumns;
    $[27] = sortedNotes;
    $[28] = tab;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <CollapsibleSection title="Memos" persistKey="memos" headerControls={t6} actions={t7}>{t8}</CollapsibleSection>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
}
function _temp3(a, b) {
  if (a.isAlert !== b.isAlert) {
    return a.isAlert ? -1 : 1;
  }
  if (a.isPinned !== b.isPinned) {
    return a.isPinned ? -1 : 1;
  }
  return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
}
function _temp2(note) {
  return note.eventId;
}
function _temp(s) {
  return s.openMemoWindow;
}